import React, { useContext, useEffect, useState, useCallback } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Container,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { WhatsApp, Visibility, VisibilityOff } from "@mui/icons-material";
import AuthContext from "../../Context/auth/AuthContext";
import imgbgregister from "../../assets/img/registration-form__bg.png";

const RESEND_COOLDOWN = 180; // 3 minutes
const steps = ["Datos personales", "Verificar cuenta"];

const Register = () => {
  const { submitRegistration, verifyRegistration } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [warningMsg, setWarningMsg] = useState("");
  const [countdown, setCountdown] = useState(0);

  // Saved registration data for step 2
  const [registrationData, setRegistrationData] = useState(null);

  // Referral code from URL
  const searchParams = new URLSearchParams(location.search);
  const referralCode = searchParams.get("referral_code");

  useEffect(() => {
    if (referralCode) {
      localStorage.setItem("referral_code", referralCode);
    }
  }, [referralCode]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const startCooldown = useCallback(() => {
    setCountdown(RESEND_COOLDOWN);
  }, []);

  // Step 1 validation
  const step1Schema = Yup.object({
    name: Yup.string()
      .min(2, "El nombre debe tener al menos 2 caracteres")
      .required("Nombre completo requerido"),
    first_lastname: Yup.string()
      .min(2, "El Apellido Paterno debe tener al menos 2 caracteres")
      .required("Apellido Paterno es requerido"),
    second_lastname: Yup.string()
      .min(2, "El Apellido Materno debe tener al menos 2 caracteres")
      .required("El Apellido Materno es requerido"),
    number_phone: Yup.string()
      .matches(/^\d{10}$/, "El teléfono debe tener exactamente 10 dígitos")
      .required("Teléfono requerido"),
    email: Yup.string().email("Correo electrónico no válido").notRequired(),
    password: Yup.string()
      .min(8, "La contraseña debe tener al menos 8 caracteres")
      .required("Contraseña requerida"),
  });

  // Step 2 validation
  const step2Schema = Yup.object({
    code: Yup.string()
      .matches(/^\d{6}$/, "El código debe tener 6 dígitos")
      .required("Código requerido"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      first_lastname: "",
      second_lastname: "",
      number_phone: "",
      email: "",
      password: "",
      referral_code:
        referralCode || localStorage.getItem("referral_code") || "",
      code: "",
    },
    validationSchema: activeStep === 0 ? step1Schema : step2Schema,
    validateOnChange: true,
    enableReinitialize: false,
    onSubmit: async (values) => {
      setLoading(true);
      setErrorMsg("");
      setWarningMsg("");

      try {
        if (activeStep === 0) {
          // Step 1: Send registration data
          const payload = {
            name: values.name,
            first_lastname: values.first_lastname,
            second_lastname: values.second_lastname,
            number_phone: values.number_phone,
            password: values.password,
            referral_code: values.referral_code || undefined,
          };
          if (values.email) payload.email = values.email;

          const result = await submitRegistration(payload);

          if (result.success) {
            setRegistrationData(payload);
            startCooldown();
            setActiveStep(1);
          } else if (result.rateLimit) {
            setWarningMsg(result.message);
          } else {
            setErrorMsg(result.message);
          }
        } else {
          // Step 2: Verify code — resend all data + code
          const payload = {
            ...registrationData,
            code: values.code,
          };

          const result = await verifyRegistration(payload);

          if (result.success) {
            localStorage.removeItem("referral_code");
            setTimeout(() => {
              history.push("/");
            }, 2200);
          } else if (result.rateLimit) {
            setWarningMsg(result.message);
          } else {
            setErrorMsg(result.message);
          }
        }
      } catch (err) {
        setErrorMsg("Ocurrió un error inesperado. Intenta de nuevo.");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleResend = async () => {
    if (!registrationData) return;
    setLoading(true);
    setErrorMsg("");
    setWarningMsg("");
    const result = await submitRegistration(registrationData);
    if (result.success) {
      startCooldown();
      Swal.fire({
        title: "Código reenviado",
        text: "Revisa tu WhatsApp.",
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
      });
    } else if (result.rateLimit) {
      setWarningMsg(result.message);
    } else {
      setErrorMsg(result.message);
    }
    setLoading(false);
  };

  const maskedPhone = formik.values.number_phone
    ? "****" + formik.values.number_phone.slice(-4)
    : "";

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 8 }}>
      <Box
        sx={{
          backgroundImage: `url(${imgbgregister})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          padding: 4,
          borderRadius: 3,
          boxShadow: 3,
        }}
      >
        <Typography variant="h4" gutterBottom textAlign="center" fontWeight="bold">
          Regístrate ahora
        </Typography>

        {/* Stepper */}
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: 3,
            "& .MuiStepIcon-root.Mui-active": { color: "#801F56" },
            "& .MuiStepIcon-root.Mui-completed": { color: "#801F56" },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Alerts */}
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg("")}>
            {errorMsg}
          </Alert>
        )}
        {warningMsg && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarningMsg("")}>
            ⏳ {warningMsg}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit} noValidate>
          {activeStep === 0 ? (
            /* ─── Step 1: Registration Form ─── */
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre(s)"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Apellido Paterno"
                  name="first_lastname"
                  value={formik.values.first_lastname}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.first_lastname &&
                    Boolean(formik.errors.first_lastname)
                  }
                  helperText={
                    formik.touched.first_lastname && formik.errors.first_lastname
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Apellido Materno"
                  name="second_lastname"
                  value={formik.values.second_lastname}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.second_lastname &&
                    Boolean(formik.errors.second_lastname)
                  }
                  helperText={
                    formik.touched.second_lastname &&
                    formik.errors.second_lastname
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  name="number_phone"
                  type="tel"
                  inputProps={{ maxLength: 10 }}
                  value={formik.values.number_phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.number_phone &&
                    Boolean(formik.errors.number_phone)
                  }
                  helperText={
                    formik.touched.number_phone && formik.errors.number_phone
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Correo electrónico (opcional)"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={
                    (formik.touched.email && formik.errors.email) ||
                    "Puedes agregarlo después si lo prefieres"
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contraseña"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {formik.values.referral_code && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Código de referido"
                    name="referral_code"
                    value={formik.values.referral_code}
                    InputProps={{ readOnly: true }}
                    sx={{ display: "none" }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                  sx={{
                    py: 1.4,
                    backgroundColor: "#801F56",
                    "&:hover": { backgroundColor: "#6a1847" },
                    fontWeight: 600,
                  }}
                >
                  {loading ? "Enviando..." : "Registrarme"}
                </Button>
              </Grid>
            </Grid>
          ) : (
            /* ─── Step 2: WhatsApp Code Verification ─── */
            <Box sx={{ textAlign: "center" }}>
              <WhatsApp
                sx={{
                  fontSize: 48,
                  color: "#25D366",
                  mb: 1,
                }}
              />
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                Verifica tu cuenta
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enviamos un código de 6 dígitos por WhatsApp al número{" "}
                <strong>{maskedPhone}</strong>. Ingrésalo para completar tu
                registro.
              </Typography>

              <TextField
                fullWidth
                label="Código de verificación"
                name="code"
                value={formik.values.code}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (val.length <= 6) formik.setFieldValue("code", val);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.code && Boolean(formik.errors.code)}
                helperText={formik.touched.code && formik.errors.code}
                inputProps={{
                  maxLength: 6,
                  style: {
                    letterSpacing: "0.5em",
                    textAlign: "center",
                    fontSize: "1.4rem",
                  },
                }}
                placeholder="000000"
                sx={{ mb: 2 }}
              />

              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={loading || formik.values.code.length !== 6}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                sx={{
                  py: 1.4,
                  backgroundColor: "#801F56",
                  "&:hover": { backgroundColor: "#6a1847" },
                  fontWeight: 600,
                }}
              >
                {loading ? "Verificando..." : "Verificar y crear cuenta"}
              </Button>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 2,
                  gap: 1,
                }}
              >
                {countdown > 0 ? (
                  <Chip
                    label={`Reenviar en ${Math.floor(countdown / 60)}:${String(
                      countdown % 60
                    ).padStart(2, "0")}`}
                    variant="outlined"
                    size="small"
                    sx={{ color: "#801F56", borderColor: "#801F56" }}
                  />
                ) : (
                  <Button
                    size="small"
                    onClick={handleResend}
                    disabled={loading}
                    startIcon={<WhatsApp fontSize="small" />}
                    sx={{
                      color: "#25D366",
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Reenviar código
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </form>

        <Typography textAlign="center" sx={{ mt: 3 }}>
          ¿Ya tienes cuenta?{" "}
          <Link
            to="/iniciar-sesion"
            style={{ color: "#801F56", fontWeight: 600 }}
          >
            Inicia sesión
          </Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default Register;
